/*    
<summary>
   This class component is all about Managing email setting.
   Developer:Aashish Singh, Created Date:13-August-2025
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/
import { action, computed, makeObservable, observable } from "mobx";
import { AuthStore } from "./auth-store";
import { ICommonState } from "../../models/state/ICommonState";
import IApiResponse, {
  IApiSuccessResponse,
} from "../../models/response/IApiResponse";
import {
  IAddUpdateHQEmailSettings,
  IHQEmailSettings,
  IHQStoreEmailSettings,
  IStoreEmailSettings,
  IUpdateStoreEmailSettings,
} from "../../models/response/IEmailManagementResponse";
import { IObservableInitialState, IOption } from "../../models/ICommon";
import toast from "react-hot-toast";
import { formatMessage } from "../../translations/format-message";
import * as baseService from "../service/base-service";
import URLConstants from "../../constants/url-constants";
import { initialState } from "../initialState/add-edit-email-setting-state";
import emailTypeEnum from "../../constants/enums/email-type-enum";

const authStore = new AuthStore();

export class EmailManagementStore implements ICommonState {
  inProgress = false;
  error = "";
  initialStateValue: IObservableInitialState = {
    success: false,
    error: "",
    inProgress: false,
  };
  hqEmailSettings: IHQEmailSettings | undefined = undefined;
  storeEmailSettings: IStoreEmailSettings | undefined = undefined;
  addUpdateEmailSettingState = { ...this.initialStateValue };
  emailType: emailTypeEnum = emailTypeEnum.ReservationReminder;
  disableAction: boolean = false;

  constructor() {
    makeObservable(this, {
      inProgress: observable,
      error: observable,
      emailType: observable,
      hqEmailSettings: observable,
      storeEmailSettings: observable,
      addUpdateEmailSettingState: observable,
      disableAction: observable,
      GetHQEmailSettingsService: action,
      AddHQEmailSettingService: action,
      UpdateHQEmailSettingService: action,
      resetGetHQEmailSettingsDetail: action,
      GetStoreEmailSettingsService: action,
      AddStoreEmailSettingService: action,
      UpdateStoreEmailSettingService: action,
      setEmailTypeEnum: action,
      resetAddUpdateEmailSettingState: action,
      resetGetStoreEmailSettingsDetail: action,
      resetStoreDetails: action,
      getHqEmailSettingDetails: computed,
      getStoreEmailSettingDetails: computed,
    });
  }

  /**
   * This function is used to Get HQEmailSettings Details by calling an API.
   * @param headquarterId : headquarter Identifier
   * @param emailType : Email Identifier
   * @returns
   */
  GetHQEmailSettingsService = (headquarterId: number, emailType: number) => {
    this.inProgress = true;
    const url =
      URLConstants.GetHQEmailSettings +
      "?headquarterId=" +
      headquarterId +
      "&emailType=" +
      emailType;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IHQEmailSettings>>) => {
        if (response.data.Error) {
          this.error = response.data.Message;
          this.disableAction = false;
          // toast.error(formatMessage(response.data.Message));
        } else {
          this.disableAction = false;
          this.hqEmailSettings = response.data.Data;
        }
      })
      .catch((err: string) => {
        this.disableAction = true;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * This function provides initail values to the Add Update email setting Form.
   * @returns Initial email setting Details
   */
  get getHqEmailSettingDetails(): IHQStoreEmailSettings {
    if (this.hqEmailSettings){
        let beforedays = this.emailType === emailTypeEnum.HappyBirthday ? Math.floor(this.hqEmailSettings.EmailBeforeHours/24) : 0;
        let beforehours = this.emailType === emailTypeEnum.HappyBirthday ? this.hqEmailSettings.EmailBeforeHours%24 : this.hqEmailSettings.EmailBeforeHours;
        return {
          Id: this.hqEmailSettings?.Id ?? -1,
          HeadquarterId: this.hqEmailSettings?.HeadquarterId ?? -1,
          EmailBeforeHours:beforehours || 0,
          EmailAfterHours: this.hqEmailSettings.EmailAfterHours || 0,
          EmailType: this.hqEmailSettings.EmailType,
          EmailBody: this.hqEmailSettings.EmailBody || "",
          EmailHeader: this.hqEmailSettings.EmailHeader || "",
          EmailFooter: this.hqEmailSettings.EmailFooter || "",
          StoreEmailBody: "",
          BeforeDays: beforedays,
          BeforeHours: beforehours || 0,
          IsSend: false,
          StoreId: -1,
        };
    }
    return initialState;
  }

  /**
   * This function is used to reset hqEmailSettings observables to their initial values.
   * @returns
   */
  resetGetHQEmailSettingsDetail = () => {
    this.hqEmailSettings = undefined;
    this.inProgress = false;
    this.error = "";
  };

  AddHQEmailSettingService = (data: IAddUpdateHQEmailSettings) => {
    this.addUpdateEmailSettingState.inProgress = true;
    let url = URLConstants.AddHQEmailSettings;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateEmailSettingState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else this.addUpdateEmailSettingState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateEmailSettingState.error = err;
        // toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateEmailSettingState.inProgress = false;
        })
      );
  };
  
  UpdateHQEmailSettingService = (data: IAddUpdateHQEmailSettings) => {
    this.addUpdateEmailSettingState.inProgress = true;
    let url = URLConstants.UpdateHQEmailSettings;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateEmailSettingState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else this.addUpdateEmailSettingState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateEmailSettingState.error = err;
        // toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateEmailSettingState.inProgress = false;
        })
      );
  };

  resetAddUpdateEmailSettingState = () => {
    this.addUpdateEmailSettingState = { ...this.initialStateValue };
  };

  /**
   * This function is used to Get Store Email Settings Details by calling an API.
   * @param headquarterId : headquarter Identifier
   * @param emailType : Email Identifier
   * @param storeId : Store Identifier
   * @returns
   */
  GetStoreEmailSettingsService = (
    headquarterId: number,
    emailType: number,
    storeId: number
  ) => {
    this.inProgress = true;
    const url =
      URLConstants.GetStoreEmailSettings +
      "?headquarterId=" +
      headquarterId +
      "&emailType=" +
      emailType +
      "&storeId=" +
      storeId;
    return baseService
      .getRequest(url)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IStoreEmailSettings>>) => {
          if (response.data.Error) {
            this.error = response.data.Message;
            // toast.error(formatMessage(response.data.Message));
          } else {
            this.disableAction = false;
            this.storeEmailSettings = response.data.Data;
          }
        }
      )
      .catch((err: string) => {
        this.disableAction = true;
         toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.inProgress = false;
        })
      );
  };

  /**
   * This function provides initail values to the Add Update email setting Form.
   * @returns Initial Email settings Details
   */
  get getStoreEmailSettingDetails(): IHQStoreEmailSettings {
    if (this.storeEmailSettings){
        let beforedays = this.emailType === emailTypeEnum.HappyBirthday ? Math.floor(this.storeEmailSettings.EmailBeforeHours/24) : 0;
        let beforehours = this.emailType === emailTypeEnum.HappyBirthday ? this.storeEmailSettings.EmailBeforeHours%24 : this.storeEmailSettings.EmailBeforeHours;
        return {
          Id: this.storeEmailSettings.Id ?? -1,
          StoreId: this.storeEmailSettings.StoreId ?? -1,
          HeadquarterId: this.storeEmailSettings.HeadquarterId ?? -1,
          EmailBeforeHours: beforehours || 0,
          EmailAfterHours: this.storeEmailSettings.EmailAfterHours || 0,
          EmailType: this.storeEmailSettings.EmailType,
          EmailBody: this.storeEmailSettings.EmailBody || "",
          EmailHeader: this.storeEmailSettings.EmailHeader || "",
          EmailFooter: this.storeEmailSettings.EmailFooter || "",
          StoreEmailBody: this.storeEmailSettings.StoreEmailBody || "",
          IsSend: this.storeEmailSettings.IsSend,
          BeforeDays: beforedays,
          BeforeHours: beforehours || 0,
        };
    }
    return initialState;
  }

  /**
   * This function is used to reset getDeviceDetail observables to their initial values.
   * @returns
   */
  resetGetStoreEmailSettingsDetail = () => {
    this.storeEmailSettings = undefined;
    this.inProgress = false;
    this.error = "";
  };

  resetStoreDetails = () => {
    this.storeEmailSettings = undefined;
    this.inProgress = false;
    this.error = "";
    this.hqEmailSettings = undefined;
    this.disableAction = false;
  };
  
  AddStoreEmailSettingService = (data: IUpdateStoreEmailSettings) => {
    this.addUpdateEmailSettingState.inProgress = true;
    let url = URLConstants.UpdateStoreEmailSettings;
    return baseService
      .postRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateEmailSettingState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else this.addUpdateEmailSettingState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateEmailSettingState.error = err;
        // toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateEmailSettingState.inProgress = false;
        })
      );
  };
  UpdateStoreEmailSettingService = (data: IUpdateStoreEmailSettings) => {
    this.addUpdateEmailSettingState.inProgress = true;
    let url = URLConstants.UpdateStoreEmailSettings;
    return baseService
      .putRequest(url, data)
      .then((response: IApiResponse<IApiSuccessResponse<boolean>>) => {
        if (response.data.Error) {
          this.addUpdateEmailSettingState.error = response.data.Message;
          // toast.error(formatMessage(response.data.Message));
        } else this.addUpdateEmailSettingState.success = true;
      })
      .catch((err: string) => {
        this.addUpdateEmailSettingState.error = err;
        // toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.addUpdateEmailSettingState.inProgress = false;
        })
      );
  };

  setEmailTypeEnum = (emailType: emailTypeEnum) =>{
    this.emailType = emailType;
  }
  
}

export default new EmailManagementStore();
